// 환생 코어 순수 규칙(레인 R, docs/REBIRTH-MODES-SPEC.md 2장): 조건·레벨 동기화·환생 포인트 효과·보상·외형 등급·윤회의 문 자리.
// 게임(game/rebirth.ts)·UI(ui/rebirth*.ts)·렌더(render/rebirthAura·rebirthGate)가 함께 읽는다. three·DOM import 금지, 할당 없는 계산만.
// 수치는 가안(스펙 2.2). tools/sim으로 확정하면 이 파일과 스펙 2장을 함께 고친다.
import { REBIRTH } from './content/items';
import { PLAYABLE_REGIONS } from './content/regionCount';
import { STAGES_PER_REGION } from './content/world';
import type { RebirthState, RpgMode } from './content/types';

/** 환생 회차(0 = 1회차 핵앤슬래시, 1 = 1차 환생 ARPG, 2 = 2차 환생 MMORPG) */
export type RebirthCycleNo = 0 | 1 | 2;
export const REBIRTH_MAX_CYCLE: RebirthCycleNo = 2;
/** 환생할 수 있는 레벨(PRD 3B) */
export const REBIRTH_LEVEL_REQ = 99;
/** 윤회의 문이 마을① 광장에 보이기 시작하는 레벨 */
export const REBIRTH_GATE_LEVEL = 90;

/**
 * 윤회의 문 퀘스트(스펙 2.1-1): 정본 목표 = 지역6 헬 보스 처치(REBIRTH_QUEST.goal). L1029 때는 헬이 없어 마지막 지역 하드 n-10으로
 * 판정했고(목표를 데이터로만 두면 운영에서 영원히 열리지 않는다), C10에서 헬 난이도가 생겨 NOW를 goal(헬 6-10)로 올렸다 —
 * 720h 시뮬(tools/sim/pacing720.ts gateDone, designed)과 같은 판정이다. 판정 입력은 헬 순차 클리어 수(stage.hell.v1).
 * `?modes=1`(QA·개발)에서는 Lv99만으로 열린다.
 */
export const REBIRTH_QUEST = { id: 'q_rebirth_gate', name: '윤회의 문', goal: { region: 6, difficulty: 'hell', stage: 10 } } as const;
export const REBIRTH_QUEST_NOW = { region: PLAYABLE_REGIONS, difficulty: 'hell', stage: STAGES_PER_REGION } as const;

/** 윤회의 문 퀘스트 완료: 헬 순차 클리어 수(지역 n의 보스 = 10n)가 마지막 플레이 지역 헬 보스까지 닿았는가 */
export function rebirthQuestDone(hellCleared: number): boolean { return hellCleared >= REBIRTH_QUEST_NOW.region * REBIRTH_QUEST_NOW.stage; }

/** 윤회의 문 자리(마을① 광장 서쪽, 상점과 교관 사이 빈 바닥). 판정 격자를 바꾸지 않는 상호작용 지점이다(길찾기·NPC 동선 그대로) */
export const REBIRTH_GATE = {
  town: 1,
  /** 상호작용 중심(걸을 수 있는 바닥). z=2.0은 동쪽 던전 문 통로(벽 사이 z 1~3, 몸 반경 0.45 → 중심 1.45~2.55)의 한가운데라 문에서 곧장 동쪽으로 걸어도 벽 모서리에 걸리지 않는다 */
  x: -13.6, z: 2.0,
  /** 아치가 서는 자리(중심 서쪽 1.4m, 동쪽을 본다) */
  archX: -15.0, archZ: 2.0, rot: Math.PI / 2,
  /** 이 거리(m) 안에서 E/터치로 확인 화면을 연다 */
  interactRadius: 2.2,
} as const;

// ── 환생 포인트 ───────────────────────────────────
/** 회차당 환생 포인트·슬롯·다음 회차 EXP(가안, PRD 3B·content/items REBIRTH_REWARD와 같은 값) */
export const REBIRTH_POINTS_PER_CYCLE = 10;
export const REBIRTH_SLOT_BONUS = 1;
export const REBIRTH_XP_BONUS = 0.5;
/** 포인트 1의 효과: 공격 +1% · 최대 HP +2% · 치명 +0.2%p */
export const REBIRTH_POINT_EFFECT = { atk: 0.01, hp: 0.02, crit: 0.002 } as const;
export type RebirthStat = keyof typeof REBIRTH_POINT_EFFECT;
export const REBIRTH_STATS: readonly RebirthStat[] = ['atk', 'hp', 'crit'];
export const REBIRTH_STAT_NAME: Readonly<Record<RebirthStat, string>> = { atk: '공격', hp: '최대 HP', crit: '치명' };
export interface RebirthAlloc { readonly atk: number; readonly hp: number; readonly crit: number }
export const EMPTY_ALLOC: RebirthAlloc = { atk: 0, hp: 0, crit: 0 };

/** 배분 합 */
export function allocSum(a: RebirthAlloc): number { return a.atk + a.hp + a.crit; }
/** 배분이 규칙을 지키는지: 음이 아닌 정수, 합 ≤ 가진 포인트 */
export function allocValid(a: unknown, points: number): a is RebirthAlloc {
  if (a === null || typeof a !== 'object') return false;
  const o = a as Record<string, unknown>;
  for (const k of REBIRTH_STATS) { const v = o[k]; if (typeof v !== 'number' || !Number.isSafeInteger(v) || v < 0) return false; }
  return allocSum(o as unknown as RebirthAlloc) <= points;
}

/** deriveStats가 받는 환생 보너스(공격 %, HP %, 치명 가산) */
export interface RebirthStatsIn { readonly atkPct: number; readonly hpPct: number; readonly crit: number }
export interface MutableRebirthStats { atkPct: number; hpPct: number; crit: number }
export function makeRebirthStats(): MutableRebirthStats { return { atkPct: 0, hpPct: 0, crit: 0 }; }
/** 배분 → 능력치 보너스(out 재사용, 할당 없음) */
export function rebirthStats(out: MutableRebirthStats, a: RebirthAlloc): MutableRebirthStats {
  out.atkPct = a.atk * REBIRTH_POINT_EFFECT.atk;
  out.hpPct = a.hp * REBIRTH_POINT_EFFECT.hp;
  out.crit = a.crit * REBIRTH_POINT_EFFECT.crit;
  return out;
}

// ── 보상 ─────────────────────────────────────────
/** 그 회차까지 받은 환생 포인트 총량 */
export function rebirthPointsAt(cycle: number): number { return Math.max(0, Math.floor(cycle)) * REBIRTH_POINTS_PER_CYCLE; }
/** 그 회차의 스킬 슬롯 추가 칸(페이지마다). 1차 환생부터 +1(6 → 7칸) */
export function rebirthSlotBonus(cycle: number): number { return cycle >= 1 ? REBIRTH_SLOT_BONUS : 0; }
/** 그 회차의 EXP 배율(다음 회차 EXP +50%, 1차 환생부터) */
export function rebirthXpMult(cycle: number): number { return cycle >= 1 ? 1 + REBIRTH_XP_BONUS : 1; }

// ── 레벨 동기화 ──────────────────────────────────
/**
 * 장비 능력치 배율 = min(1, (현재 레벨 + 10) / 아이템 레벨). 착용 제한은 없고 레벨이 오르면 원래 수치로 돌아온다(스펙 2.2).
 * level 0 이하 = 동기화 없음(1회차는 쓰지 않는다 — 1회차 밸런스 그대로). 아이템 레벨이 0 이하이면 1
 */
export const LEVEL_SYNC_SLACK = 10;
export function levelSyncMult(level: number, ilvl: number): number {
  if (!(level > 0) || !(ilvl > 0)) return 1;
  return Math.min(1, (level + LEVEL_SYNC_SLACK) / ilvl);
}

// ── 조건 ─────────────────────────────────────────
export type RebirthDenial = 'max_cycle' | 'locked' | 'level' | 'quest';
export interface RebirthCheck {
  readonly level: number;
  readonly cycle: number;
  /** 윤회의 문 퀘스트 완료(지역6 헬 보스) */
  readonly questDone: boolean;
  /** `?modes=1`: 미공개 모드도 열고 퀘스트 없이 Lv99만으로 연다(QA·개발) */
  readonly modes: boolean;
}
/** 다음 회차 정의(없으면 undefined) */
export function nextCycleDef(cycle: number): (typeof REBIRTH)[number] | undefined {
  for (let i = 0; i < REBIRTH.length; i++) if (REBIRTH[i].cycle === cycle + 1) return REBIRTH[i];
  return undefined;
}
/** 다음 회차 모드가 공개됐는지(운영 스위치 REBIRTH[].released, `?modes=1`이면 열림) */
export function nextModeOpen(cycle: number, modes: boolean): boolean {
  const n = nextCycleDef(cycle);
  return !!n && (n.released || !!n.fallback?.released || modes);
}
/** 환생 거절 사유. null = 환생 가능. 순서: 회차 끝 → 모드 미공개 → 레벨 → 퀘스트 */
export function rebirthDenial(c: RebirthCheck): RebirthDenial | null {
  if (c.cycle >= REBIRTH_MAX_CYCLE || !nextCycleDef(c.cycle)) return 'max_cycle';
  if (!nextModeOpen(c.cycle, c.modes)) return 'locked';
  if (c.level < REBIRTH_LEVEL_REQ) return 'level';
  if (!c.questDone && !c.modes) return 'quest';
  return null;
}
/** 윤회의 문이 마을① 광장에 보이는지: Lv90 이상이고 다음 모드가 열려 있다(미공개 모드는 문 자체를 숨긴다) */
export function rebirthGateVisible(level: number, cycle: number, modes: boolean, town: number): boolean {
  return town === REBIRTH_GATE.town && level >= REBIRTH_GATE_LEVEL && nextModeOpen(cycle, modes);
}
export const REBIRTH_DENIAL_TEXT: Readonly<Record<RebirthDenial, string>> = {
  max_cycle: '이미 마지막 회차예요.',
  locked: '다음 모드는 아직 열리지 않았어요.',
  level: 'Lv.' + REBIRTH_LEVEL_REQ + '에 환생할 수 있어요.',
  quest: '「윤회의 문」 퀘스트(지역' + PLAYABLE_REGIONS + ' 헬 보스 처치)를 마쳐야 해요.',
};

// ── 외형 등급 ────────────────────────────────────
/** 회차 → 외형: 이름표 옆 별 수, 오라 색(1차 청금, 2차 진홍), 날개(1차부터 — 모델은 후속 TODO) */
export interface RebirthLook { readonly stars: number; readonly aura: number; readonly auraCss: string; readonly wings: boolean }
const LOOKS: readonly RebirthLook[] = [
  { stars: 0, aura: 0, auraCss: '', wings: false },
  { stars: 1, aura: 0x3f6fe0, auraCss: '#5b8cff', wings: true },
  { stars: 2, aura: 0xd0213a, auraCss: '#ff4a5e', wings: true },
];
export function rebirthLook(cycle: number): RebirthLook { return LOOKS[Math.max(0, Math.min(LOOKS.length - 1, Math.floor(cycle) || 0))]; }
/** 회차 별 문구(이름표 옆). 0회차는 빈 문자열 */
export function rebirthStars(cycle: number): string { return '★'.repeat(rebirthLook(cycle).stars); }

// ── 모드 ─────────────────────────────────────────
/**
 * 회차의 지금 모드: 공개된 본 모드, 아니면 공개된 대체 모드(ARPG ★2), `?modes=1`(미리보기)이면 본 모드 그대로(MMO QA).
 * 저장에 적힌 모드는 쓰지 않는다 — 공개 스위치가 바뀌면 같은 저장이 새 모드로 읽힌다(데이터 이관 없음)
 */
export function modeOfCycle(cycle: number, modes = false, fb?: string): RpgMode {
  // 이 영웅이 ★2로 고정됐으면(fb) MMO 공개·미리보기와 무관하게 ARPG(PR #330 리뷰 P2-1). 매 프레임 불리므로 클로저 없이 찾는다(P2-4)
  if (fb === STAR2_FB && cycle === 2) return 'arpg';
  const r = cycleDef(cycle);
  if (!r) return 'hackslash';
  if (r.released || modes || !r.fallback?.released) return r.mode;
  return r.fallback.mode;
}
/** 회차 이름(카드·소개 문구): 대체 모드면 그 이름(ARPG ★2) */
export function cycleModeName(cycle: number, modes = false, fb?: string): string {
  if (fb === STAR2_FB && cycle === 2) return STAR2_NAME;
  const r = cycleDef(cycle);
  if (!r) return MODE_NAME.hackslash;
  return r.released || modes || !r.fallback?.released ? MODE_NAME[r.mode] : r.fallback.name;
}
/** 고정 대체 모드 표시(HeroRebirth.fb): 2차 환생을 ARPG ★2로 한 영웅. MMO가 공개돼도 이 영웅은 ★2로 남는다 */
export const STAR2_FB = 'arpg2';
export const STAR2_NAME = 'ARPG ★2';
/** 다음 회차로 환생할 때 고정 대체 모드를 적어야 하는가: 본 모드 미공개 + 대체 공개 + 미리보기 아님 */
export function rebirthFallbackFb(nextCycle: number, preview: boolean): string | undefined {
  const r = cycleDef(nextCycle);
  return r && !r.released && !preview && r.fallback?.released && nextCycle === 2 ? STAR2_FB : undefined;
}
function cycleDef(cycle: number): (typeof REBIRTH)[number] | undefined {
  for (let i = 0; i < REBIRTH.length; i++) if (REBIRTH[i].cycle === cycle) return REBIRTH[i];
  return undefined;
}
/** ★2 소개 카드(MODE_INTRO.arpg 대신) */
export const STAR2_INTRO = { title: 'ARPG ★2 — 두 번째 오픈 필드', lines: ['같은 필드·균열에서 다시 Lv1부터: 재육성은 1회차 시간의 25%.', '대균열 단계 상한 +20, 초월 강화 +18 해금.', '1회차 마을·스테이지에도 그대로 들어갈 수 있어요.'] } as const;
export const MODE_NAME: Readonly<Record<RpgMode, string>> = { hackslash: '핵앤슬래시', arpg: 'ARPG', mmo: 'MMORPG' };
/** 새 모드 소개 카드 문구(환생 뒤 첫 화면). 필드 자체는 레인 F가 만든다 */
export const MODE_INTRO: Readonly<Record<RpgMode, { readonly title: string; readonly lines: readonly string[] }>> = {
  hackslash: { title: '핵앤슬래시', lines: ['마을과 스테이지를 오가며 사냥한다.'] },
  arpg: { title: 'ARPG — 이어진 오픈 필드', lines: ['지역이 하나로 이어진 필드를 돌며 사냥하고, 균열 조각을 모아 균열에 들어간다.', '필드 입구는 마을① 북쪽 새 문이에요.', '1회차 마을·스테이지에도 그대로 들어갈 수 있어요.'] },
  mmo: { title: 'MMORPG — 공유 필드', lines: ['다른 모험가가 보이는 필드에서 함께 사냥하고 월드 보스를 친다.', '1회차 마을·스테이지에도 그대로 들어갈 수 있어요.'] },
};

/** 환생으로 이어지는 것·초기화되는 것(확인 화면 목록, 스펙 2.1-2) */
export const REBIRTH_KEEPS: readonly string[] = ['스킬(습득 상태)', '장비·강화·보석', '펫', '골드·재화', '창고'];
export const REBIRTH_RESETS: readonly string[] = ['레벨 → 1', '스토리 진행(새 모드로)', '스킬 트리 포인트(재분배)'];

/** 환생 연산의 결과 상태(순수): cycle+1, 모드, 포인트 +10, 시각. 다른 칸은 그대로 */
export function rebornState(s: RebirthState, at: number): RebirthState {
  const cycle = Math.min(REBIRTH_MAX_CYCLE, s.cycle + 1) as RebirthCycleNo;
  return { heroId: s.heroId, cycle, mode: modeOfCycle(cycle), points: s.points + REBIRTH_POINTS_PER_CYCLE, perks: s.perks, rebornAt: at };
}

// ── 뷰·결과 코드(GameView.rebirth) ──────────────────────
export type RebirthSaveMode = 'ok' | 'future' | 'corrupt' | 'unavailable';
export type RebirthCode = 'ok' | 'duplicate' | 'readonly' | 'save_failed' | 'bad_alloc' | 'stale' | 'paused' | 'no_menu' | RebirthDenial;
export interface RebirthLastView {
  readonly seq: number;
  readonly kind: 'none' | 'rebirth' | 'alloc' | 'intro';
  readonly code: RebirthCode;
  readonly requestId: string;
  readonly message: string;
}
/** 지금 영웅의 환생 상태(읽기 전용, 살아 있는 getter) */
export interface RebirthView {
  readonly mode: RebirthSaveMode;
  /** 바뀔 때마다 오른다(UI 재그리기 키) */
  readonly revision: number;
  readonly cycle: number;
  readonly modeId: RpgMode;
  readonly modeName: string;
  /** 받은 환생 포인트 총량·배분·남은 포인트 */
  readonly points: number;
  readonly alloc: RebirthAlloc;
  readonly free: number;
  /** 이어 가는 스킬 습득 비트 */
  readonly kept: number;
  readonly rebornAt: number | null;
  /** 윤회의 문이 지금 마을에 보이는가(마을①, Lv90+, 다음 모드 열림) */
  readonly gateVisible: boolean;
  /** 지금 환생 거절 사유(null = 가능) */
  readonly denial: RebirthDenial | null;
  /** `?modes=1`(미공개 모드 열림) */
  readonly modes: boolean;
  /** 새 모드 소개 카드를 아직 안 봤다(환생 직후 첫 화면) */
  readonly introPending: boolean;
  /** 이번 회차 EXP 배율·스킬 슬롯 추가 칸 */
  readonly xpMult: number;
  readonly slotBonus: number;
  readonly last: RebirthLastView;
}
